import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Container,
    FilterBar,
    SectionHeader,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    StatusBadge,
    StatCard,
    StatCardGroup,
} from "@athena/ui";
import { Download, Pencil, Trash2, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import {
    UsersDocument,
    DeleteUserDocument,
    UsersCountDocument,
    PendingAutoEnrolledUsersDocument,
    type UsersQuery,
} from "../../graphql/users.generated";
import type { Role, UserStatus } from "@athena/common/graphql/generated/schema";
import { formatDateTime } from "../../../../global/formatHelpers";
import { DeleteDialog } from "@/components/DeleteDialog";
import { InviteUserDialog } from "./InviteUserDialog";
import { UserTable, userColumns, type UserRow } from "@/components/tables/UserTable";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { roleLabelMap } from "@/types/Roles";
import { STATUS_LABEL } from "@/types/Status";
import { exportCsv } from "@/lib/exportCsv";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { DEFAULT_PAGE_SIZE, useWindowedRows } from "@/hooks/useWindowedRows";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

/** `MAX_LIMIT` in the server's pagination helper — the largest page it will honour. */
const EXPORT_PAGE_SIZE = 200;

/**
 * A ceiling on the export, so a mis-scoped filter cannot pull the whole users table into a browser
 * tab. 50,000 rows is far past any real export and still an order of magnitude under the platform
 * total, which is exactly the case worth stopping.
 */
const EXPORT_MAX_ROWS = 50_000;

/** Invites have no account yet; this bounds the supplementary directory query, not an export. */
const PENDING_INVITE_LIMIT = 200;

/** Sentinel for "no filter". Radix Select has no concept of an unset value, only of a chosen one. */
const ALL = "__all__";
const INVITED = "INVITED";

/** Every role a directory row can hold. SYSTEM included: it is rare, which is why filtering finds it. */
const ROLES: Role[] = [
    "USER",
    "COACH",
    "ORG_ADMIN",
    "SUB_ORG_ADMIN",
    "ADMIN",
    "SYSTEM",
];

const STATUSES: UserStatus[] = [
    "ACTIVE",
    "INACTIVE",
    "ARCHIVED",
];

type User = { id: string; firstName?: string | null; lastName?: string | null; email: string };

const CSV_HEADERS = [
    "First Name",
    "Last Name",
    "Email",
    "Role",
    "Status",
    "Organization",
    "Sub-organization",
    "Last Active",
    "Created At",
];

/** A membership's org, split into (root, sub) — `subOrg` is null for a top-level org. */
const getOrgHierarchy = (user: UsersQuery["users"][number]) => {
    const primaryMembership = user.memberships[0];
    const org = primaryMembership?.organization;
    const subOrg = org?.parentOrganization ? org : null;
    const rootOrg = subOrg ? org?.parentOrganization : org;
    return { rootOrg, subOrg };
};

const userToCsvRow = (user: UsersQuery["users"][number]): string[] => {
    const { rootOrg, subOrg } = getOrgHierarchy(user);
    return [
        user.firstName ?? "",
        user.lastName ?? "",
        user.email,
        roleLabelMap[user.role],
        STATUS_LABEL[user.status],
        rootOrg?.name ?? "",
        subOrg?.name ?? "",
        user.lastActiveAt ? formatDateTime(user.lastActiveAt) : "",
        formatDateTime(user.createdAt),
    ];
};

export function UsersPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const { isPlatformAdmin } = usePlatformAdmin();
    const [
        inviteOpen,
        setInviteOpen,
    ] = useState<boolean>(false);

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        roleFilter,
        setRoleFilter,
    ] = useState<string>(ALL);
    const [
        statusFilter,
        setStatusFilter,
    ] = useState<string>(ALL);
    const [
        orgFilter,
        setOrgFilter,
    ] = useState<string>(ALL);

    /**
     * **Filtered by the server, not in the browser.** `users` returns at most 50 rows by default, so
     * a client-side filter would have searched one page and confidently reported nothing for anyone
     * outside it. `UserFilterInput` — search across email and both names, plus role, status and
     * organization — has existed in the schema and the repository since the directory was built and
     * had no caller.
     */
    const settledSearch = useDebouncedValue(search);
    /**
     * Debounced while typing, immediate when emptied. Clearing is a discrete action, not a keystroke
     * — waiting out the delay left the Clear button on screen and the narrowed request in flight for
     * a third of a second after the operator had already cleared the box.
     */
    const queryTerm = search.trim() === "" ? "" : settledSearch.trim();
    const invitedOnly = statusFilter === INVITED;
    const filter = useMemo(
        () => ({
            ...(queryTerm ? { search: queryTerm } : {}),
            ...(roleFilter !== ALL ? { role: roleFilter as Role } : {}),
            ...(statusFilter !== ALL && statusFilter !== INVITED ? { status: statusFilter as UserStatus } : {}),
            ...(orgFilter !== ALL ? { organizationId: orgFilter } : {}),
        }),
        [
            queryTerm,
            roleFilter,
            statusFilter,
            orgFilter,
        ],
    );

    /*
     * A window onto the users, not all of them.
     *
     * This page reads a table that is 85,371 rows in production. It previously asked for one
     * unbounded page, got `normalizePagination`'s default of 50, and reported that as the total.
     * Fetching everything instead would be worse — the fix is to hold a page and grow it as
     * somebody scrolls, with the count coming from the server.
     */
    const {
        rows: users,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        refetch,
    } = useWindowedRows<UsersQuery["users"][number]>(UsersDocument, (d) => (d as UsersQuery | undefined)?.users, {
        variables: { filter },
    });

    /*
     * Auto-enrollment intentionally does not create a User until first sign-in.
     * The organization roster already renders those records as Invited; the global
     * directory needs the same second source or an invite appears to disappear.
     * Role and account-status filters apply only to accounts, so invitations are
     * omitted while either is selected rather than pretending they are inactive.
     */
    const pendingFilter = useMemo(
        () => ({
            ...(queryTerm ? { search: queryTerm } : {}),
            ...(orgFilter !== ALL ? { organizationId: orgFilter } : {}),
        }),
        [
            queryTerm,
            orgFilter,
        ],
    );
    const { data: pendingData, loading: pendingLoading } = useQuery(PendingAutoEnrolledUsersDocument, {
        variables: { filter: pendingFilter, limit: PENDING_INVITE_LIMIT },
    });
    const invitationsVisible = roleFilter === ALL && (statusFilter === ALL || invitedOnly);
    const invitedCount = invitationsVisible ? pendingData?.pendingAutoEnrolledUsersCount : 0;

    // The real total, under the same access rules and the same filter as the list above.
    const { data: countData } = useQuery(UsersCountDocument, { variables: { filter } });
    const accountCount = invitedOnly ? 0 : countData?.usersCount;
    const matchingCount =
        accountCount === undefined || invitedCount === undefined ? undefined : accountCount + invitedCount;

    /*
     * "Active" is its own count, not a filter over the rows on screen.
     *
     * It used to be `users.filter(status === ACTIVE).length`, which described the loaded page. With
     * a window that is whatever has scrolled by, which would make the number climb as you scroll —
     * visibly wrong in a way the old version at least hid behind a fixed 50.
     */
    const { data: activeCountData } = useQuery(UsersCountDocument, {
        variables: { filter: { ...filter, status: "ACTIVE" as UserStatus } },
    });
    const activeCount = invitedOnly ? 0 : activeCountData?.usersCount;

    /*
     * One stream: every account, then every invitation.
     *
     * Joined before the view rather than after it. `view.rows` is one page's worth in pages mode,
     * so appending the invitations to it would repeat all of them under every page.
     */
    const accounts = invitedOnly ? [] : users;
    const invitees = invitationsVisible ? (pendingData?.pendingAutoEnrolledUsers ?? []) : [];
    const combined = [
        ...accounts.map((user) => ({ kind: "account" as const, user })),
        ...invitees.map((invitee) => ({ kind: "invitee" as const, invitee })),
    ];

    /* Only the accounts are a paged read — the invitations arrive whole, up to their own limit. */
    const effectiveHasMore = !invitedOnly && hasMore;

    /*
     * Scroll or pages, the reader's choice, portal-wide. Both read the same window: the sentinel
     * and the Next button are two ways of asking `useWindowedRows` for the next page.
     */
    const view = useListView(combined, { hasMore: effectiveHasMore, loadMore, pageSize: DEFAULT_PAGE_SIZE });

    const sentinelRef = useInfiniteScroll(loadMore, hasMore);
    const filtersActive = Object.keys(filter).length > 0 || invitedOnly;

    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState<User | null>(null);
    const [
        deleteUser,
        { loading: deleting },
    ] = useMutation(DeleteUserDocument);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        await deleteUser({
            variables: { id: deleteTarget.id },
            update(cache) {
                cache.evict({ id: cache.identify({ __typename: "User", id: deleteTarget.id }) });
                cache.gc();
            },
        });
        setDeleteTarget(null);
    };

    /**
     * The query's shape mapped onto the columns' shape. One place, and the same `getOrgHierarchy`
     * the CSV export already used — so the table and the export cannot disagree about which
     * organization a person belongs to.
     */
    const rows: UserRow[] = view.rows.map((entry) => {
        if (entry.kind === "invitee") {
            const { invitee } = entry;
            const subOrg = invitee.organization.parentOrganization ? invitee.organization : null;
            const rootOrg = subOrg ? invitee.organization.parentOrganization : invitee.organization;
            return {
                id: `pending:${invitee.id}`,
                firstName: invitee.firstName,
                lastName: invitee.lastName,
                email: invitee.email,
                phoneNumber: invitee.phoneNumber,
                createdAt: invitee.createdAt,
                organizationName: rootOrg?.name ?? null,
                subOrganizationName: subOrg?.name ?? null,
                isPendingInvite: true,
                statusNode: (
                    <StatusBadge dot variant="muted">
                        Invited
                    </StatusBadge>
                ),
            };
        }
        const { rootOrg, subOrg } = getOrgHierarchy(entry.user);
        return {
            ...entry.user,
            organizationName: rootOrg?.name ?? null,
            subOrganizationName: subOrg?.name ?? null,
        };
    });

    /*
     * Export every matching user, not the ones on screen.
     *
     * This mapped over the loaded rows, which was already wrong at a fixed 50 and is worse against
     * a window: the file would contain however far somebody happened to have scrolled, with no
     * indication it was partial. "Export" means the filtered set, so this pages the query itself
     * rather than reading the table's state.
     *
     * Straight through the client, not the hook: the hook's job is to keep the *table* small, and
     * feeding a full export through it would grow the very list this page went windowed to bound.
     */
    const [
        exporting,
        setExporting,
    ] = useState(false);

    const handleExportCsv = async () => {
        setExporting(true);
        try {
            const all: UsersQuery["users"] = [];
            for (let offset = 0; offset < EXPORT_MAX_ROWS; offset += EXPORT_PAGE_SIZE) {
                const { data: page } = await client.query<UsersQuery>({
                    query: UsersDocument,
                    variables: { filter, limit: EXPORT_PAGE_SIZE, offset },
                    // Never from the cache: the cached list is the window this page is showing,
                    // and reading it back would export the same partial set by a longer route.
                    fetchPolicy: "no-cache",
                });
                const rowsPage = page?.users ?? [];
                all.push(...rowsPage);
                if (rowsPage.length < EXPORT_PAGE_SIZE) break;
            }
            exportCsv("users", CSV_HEADERS, all.map(userToCsvRow));
        } finally {
            setExporting(false);
        }
    };

    const clearFilters = () => {
        setSearch("");
        setRoleFilter(ALL);
        setStatusFilter(ALL);
        setOrgFilter(ALL);
    };

    // No page-level skeleton: `UserTable` shows one in place of its own rows, so the header and the
    // filter bar are on screen from the first paint instead of the whole page blinking. It also only
    // does so when there is nothing to show, which is what keeps a settled keystroke from replacing
    // the results the operator is reading.

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={t("admin.manageUsers")}
                title={t("nav.users")}
                description={t("admin.viewAllUsers")}
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex w-auto items-center gap-1.5"
                            onClick={() => void handleExportCsv()}
                            disabled={users.length === 0 || exporting || invitedOnly}
                        >
                            <Download className="h-3.5 w-3.5" />
                            {exporting ? "Exporting…" : t("admin.exportCsv")}
                        </Button>
                        {/* An org admin may now create a member of an organization they administer,
                            so the button is theirs too — the dialog requires an org for them. */}
                        <Button
                            size="sm"
                            className="flex w-auto items-center gap-1.5"
                            onClick={() => setInviteOpen(true)}
                        >
                            <UserPlus className="h-3.5 w-3.5" />
                            Invite a user
                        </Button>
                    </div>
                }
            />

            <StatCardGroup className="mb-4 w-full">
                {/* "Matching" rather than "Total" while narrowed, because the number is the size of
                    the answer and not the size of the directory. */}
                <StatCard
                    label={filtersActive ? "Matching" : "Total"}
                    value={matchingCount === undefined ? "—" : matchingCount.toLocaleString()}
                />
                <StatCard label="Active" value={activeCount === undefined ? "—" : activeCount.toLocaleString()} />
                <StatCard label="Invited" value={invitedCount === undefined ? "—" : invitedCount.toLocaleString()} />
            </StatCardGroup>

            <FilterBar
                search={search}
                onSearchChange={setSearch}
                searchLabel="Search users"
                searchPlaceholder="Search by name or email"
                active={filtersActive}
                onClear={clearFilters}
            >
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-44" aria-label="Filter by role">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All roles</SelectItem>
                        {ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                                {roleLabelMap[role]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" aria-label="Filter by status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All statuses</SelectItem>
                        {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                                {STATUS_LABEL[status]}
                            </SelectItem>
                        ))}
                        <SelectItem value={INVITED}>Invited</SelectItem>
                    </SelectContent>
                </Select>

                {/* Only a platform admin sees more than one organization, so only they need the
                    filter — and rendering it is what fetches the list, inside the component. */}
                {isPlatformAdmin && (
                    <OrganizationSelect
                        value={orgFilter}
                        onValueChange={setOrgFilter}
                        className="w-52"
                        aria-label="Filter by organization"
                        leadingOptions={[{ value: ALL, label: "All organizations" }]}
                    />
                )}
            </FilterBar>

            <ListViewControls view={view} noun="users" />

            <UserTable
                columns={[
                    userColumns.identity,
                    { ...userColumns.organization, header: t("common.fields.org") },
                    { ...userColumns.role, header: t("common.fields.role") },
                    { ...userColumns.status, header: t("common.fields.status") },
                    userColumns.appDownloaded,
                    userColumns.lastActive,
                ]}
                rows={rows}
                rowKey={(user) => user.id}
                onRowClick={(user) => {
                    if (!user.isPendingInvite) navigate(`/users/${user.id}`);
                }}
                filtered={filtersActive}
                loading={(invitedOnly ? false : loading) || pendingLoading}
                virtualize
                noMatches="No users match this search."
                actions={(user) =>
                    user.isPendingInvite ? null : (
                        <div className="flex items-center justify-end gap-1">
                            <Button
                                variant="outline"
                                size="icon-sm"
                                className="text-muted-foreground hover:text-foreground"
                                aria-label={`Edit ${user.firstName ?? user.email}`}
                                onClick={() => navigate(`/users/${user.id}`)}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon-sm"
                                className="text-muted-foreground hover:text-destructive"
                                aria-label={`Delete ${user.firstName ?? user.email}`}
                                onClick={() => setDeleteTarget(user)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    )
                }
            />

            {/*
             * What drives the next page. An empty element after the last row: when it approaches
             * the viewport `useInfiniteScroll` calls `loadMore`, so the fetch starts while the
             * reader is still a few rows short of the bottom.
             *
             * Rendered only while there is more to fetch, so at the end of the list the observer is
             * torn down rather than left watching an element that will never mean anything again.
             */}
            {view.mode === "scroll" && effectiveHasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}

            {!invitedOnly && loadingMore && (
                <div className="text-muted-foreground py-4 text-center text-xs">Loading more…</div>
            )}
            {view.mode === "scroll" &&
                !effectiveHasMore &&
                !loading &&
                rows.length > 0 &&
                accountCount !== undefined && (
                    <div className="text-muted-foreground py-4 text-center text-xs">
                        Showing all {accountCount.toLocaleString()} accounts
                        {invitees.length > 0 && ` and ${invitees.length.toLocaleString()} invited`}
                    </div>
                )}

            <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} onCreated={() => void refetch()} />

            <DeleteDialog
                open={!!deleteTarget}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete User"
                deleteTarget={deleteTarget?.firstName ?? deleteTarget?.email}
                onConfirm={handleDelete}
                loading={deleting}
            />
        </Container>
    );
}
